<template>
    <!-- Favorite Menus-->
    <FavoriteMenuListWrapper :floating="props.floating">
        <DraggableWrapper 
            :x="position.x" :y="position.y" 
            :enabled="props.floating && !isSortMode" 
            :handleOnly="true"
            @update:position="updatePosition"
            :positionType="props.floating ? 'fixed' : 'relative'"
            :constrainToViewport="true"
            :class="[{'z-[50]': props.floating}]">
            <div class="flex flex-row items-center w-full min-w-0 gap-2 border-transparent z-[50]"
                :class="[props.class, { 'floating dark': props.floating }]">
                <div v-if="favoriteMenus.length === 0" class="border border-dashed border-gray-200 dark:border-gray-600 rounded-md p-2 text-gray-500 dark:text-gray-400"
                :class="{ 'border-transparent': props.floating }">No favorite menus</div>
                <div v-if="state === 'loading'" class="border border-dashed border-gray-200 dark:border-gray-600 rounded-md p-2 text-gray-500 dark:text-gray-400 animate-pulse">Loading...</div>
                <div v-if="favoriteMenus.length > 0 && state === 'loaded'" 
                    class="flex flex-row items-center w-full min-w-0 gap-2">
                    <button class="handle text-gray-500 dark:text-gray-400" v-if="props.floating">
                        <ArrowsPointingOutIcon class="size-6 cursor-grab" />
                    </button>
                    <Tooltip text="Sort favorite menus" position="bottom" class="flex-none flex-shrink-0">
                        <button
                            v-if="favoriteMenus.length > 0"
                            @click="toggleSortMode"
                            :class="[
                                'flex items-center justify-center p-1.5 rounded-md transition-all duration-200 dark:border-transparent dark:bg-gray-800 dark:text-white dark:hover:text-white',
                                isSortMode 
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md dark:bg-indigo-600 dark:text-white dark:hover:bg-indigo-700' 
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                            ]"
                            :title="isSortMode ? 'Done sorting' : 'Sort menus'"
                        >
                            <BarsArrowUpIcon v-if="!isSortMode" class="w-4 h-4" />
                            <CheckIcon v-else class="w-4 h-4" />
                        </button>
                    </Tooltip>
                    <div 
                        ref="menuContainerRef"
                        class="flex flex-row items-center gap-2 overflow-x-auto no-scrollbar min-w-0 flex-1 py-1 px-2"
                        :class="{ 'cursor-move': isSortMode }"
                    >
                        <div 
                            v-for="menu in favoriteMenus" 
                            :key="menu.id"
                            class="flex-shrink-0"
                            :class="{ 'cursor-move': isSortMode }"
                        >
                            <Transition name="fade">
                                <component :is="menu.component" 
                                    :ref="(el) => setMenuRef(el, menu.id)"
                                    v-bind="menu.props || {}"
                                    v-on="isSortMode ? {} : (menu.events || {})" />
                            </Transition>
                        </div>
                    </div>
                </div>
            </div>
        </DraggableWrapper>
    </FavoriteMenuListWrapper>
</template>

<script setup>
import { ref, defineProps, defineEmits, defineExpose, watch, onMounted, onUnmounted, nextTick, onBeforeUpdate } from 'vue'
import { useMenus, fetchFavoriteMenuIds, saveFavoriteMenuIds, listAccessMenusRestricted } from '@/components/common/menus/menus.js'
import Sortable from 'sortablejs'
import { BarsArrowUpIcon, CheckIcon, ArrowsPointingOutIcon } from '@heroicons/vue/24/outline'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import appStore from '@/store/app.store';
import { appEvents } from '@/store/app.events';
import DraggableWrapper from '@/components/common/elements/DraggableWrapper.vue'
import FavoriteMenuListWrapper from '@/components/common/menus/FavoriteMenuListWrapper.vue'

const FLOATING_MENU_POSITION_KEY = 'favorite-menu-position';

const emit = defineEmits(['event'])
const menuRefs = ref({});
const position = ref({ x: 0, y: 0 });

const savePosition = (pos) => {
    try {
        localStorage.setItem(FLOATING_MENU_POSITION_KEY, JSON.stringify(pos));
    } catch (error) {
        console.error('Error saving menu position:', error);
    }
}

const loadPosition = () => {
    try {
        const saved = localStorage.getItem(FLOATING_MENU_POSITION_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            return parsed;
        }
    } catch (error) {
        console.error('Error loading menu position:', error);
    }
    return null;
}

onBeforeUpdate(() => {
  menuRefs.value = {}
})

const setMenuRef = (el, menuId) => {
  if (el) {
    menuRefs.value[menuId] = el
  }
}
const { chordsMenu, scrollControlMenu, columnsMenu,
    downloadMenu, duplicateMenu, editSongMenu, playMenu,
    presentationMenu, notesMenu, transposeMenu,
    userNotesMenu, studioMenu, zoomInOutMenu, drawingMenu, shareMenu, stageSyncMenu, sendPlaylistToTeamMenu, songMapMenu, chordDiagramsMenu,
    tunerMenu, metronomeMenu
 } = useMenus(emit)

const allMenus = ref([
    chordsMenu,
    scrollControlMenu,
    columnsMenu,
    downloadMenu,
    duplicateMenu,
    editSongMenu,
    playMenu,
    presentationMenu,
    notesMenu,
    studioMenu,
    transposeMenu,
    userNotesMenu,
    zoomInOutMenu,
    drawingMenu,
    shareMenu,
    stageSyncMenu,
    sendPlaylistToTeamMenu,
    songMapMenu,
    chordDiagramsMenu,
    tunerMenu,
    metronomeMenu
])

const favoriteMenus = ref([]);
const menuContainerRef = ref(null);
const isSortMode = ref(false);
let sortableInstance = null;

const props = defineProps({
    menus: {
        type: Array,
        required: true
    },
    class: {
        type: String,
        default: ''
    },
    floating: {
        type: Boolean,
        default: false
    }, 
    allowAccess: {
        type: Boolean,
        default: false
    }
});

const favoriteMenuIds = ref([]);
const state = ref('loading');

const loadFavorites = () => {
    state.value = 'loading';
    favoriteMenuIds.value = fetchFavoriteMenuIds()
    
    // Use the order from favoriteMenuIds array to organize menus
    // The order in the array represents the display order
    favoriteMenus.value = filterMenus();
    state.value = 'loaded';
    
}

const filterMenus = () => {
    let menusToShow = favoriteMenuIds.value
        .map(id => allMenus.value.find(menu => menu.id === id))
        .filter(menu => menu !== undefined);
    if (!props.allowAccess) {
        menusToShow = menusToShow.filter(menu => !listAccessMenusRestricted.includes(menu.id));
    }
    return menusToShow;
}

watch(() => props.allowAccess, () => {
    favoriteMenus.value = filterMenus();
});

const toggleSortMode = () => {
   
    isSortMode.value = !isSortMode.value;
    appStore.state.disableEvents = isSortMode.value;
    if (isSortMode.value) {
        nextTick(() => {
            initSortable();
        });
    } else {
        destroySortable();
    }
}

const initSortable = () => {
    if (!menuContainerRef.value || sortableInstance) return;
    
    sortableInstance = Sortable.create(menuContainerRef.value, {
        animation: 150,
        ghostClass: 'opacity-50',
        chosenClass: 'cursor-grabbing',
        dragClass: 'cursor-grabbing',
        onEnd: (evt) => {
            const { oldIndex, newIndex } = evt;
            if (oldIndex !== newIndex && oldIndex !== undefined && newIndex !== undefined) {
                // Reorder the menus array
                const movedMenu = favoriteMenus.value.splice(oldIndex, 1)[0];
                favoriteMenus.value.splice(newIndex, 0, movedMenu);
                
                // Update favoriteMenuIds to match the new order and save to localStorage
                favoriteMenuIds.value = favoriteMenus.value.map(menu => menu.id);
                saveFavoriteMenuIds(favoriteMenuIds.value);
            }
        }
    });
}

const destroySortable = () => {
    if (sortableInstance) {
        sortableInstance.destroy();
        sortableInstance = null;
    }
}

const calculateDefaultPosition = () => {
    const width = 300; // estimated/average width of menu bar
    const defaultX = Math.max(16, Math.round((window.innerWidth - width) / 2));
    const defaultY = Math.max(16, window.innerHeight - 80);
    return { x: defaultX, y: defaultY };
};

onMounted(() => {
    appEvents.on('toggleFloatingMenu', (value) => {
        if (value) {
            const savedPosition = loadPosition();
            if (savedPosition && savedPosition.x !== undefined && savedPosition.y !== undefined) {
                position.value = savedPosition;
            } else {
                position.value = calculateDefaultPosition();
            }
        } else {
            position.value = { x: 0, y: 0 };
        }
    });
    
    // Initialize position if floating menu is already enabled
    if (props.floating) {
        const savedPosition = loadPosition();
        if (savedPosition && savedPosition.x !== undefined && savedPosition.y !== undefined) {
            position.value = savedPosition;
        } else {
            position.value = calculateDefaultPosition();
        }
    }
    
    loadFavorites();
});

onUnmounted(() => {
    destroySortable();
});

watch( () => props.menus, (newVal) => {
    
    if (!newVal) return;
    if (favoriteMenuIds.value.includes(newVal.id)) {
        // Menu was added - maintain order from favoriteMenuIds
        favoriteMenus.value = favoriteMenuIds.value
            .map(id => allMenus.value.find(menu => menu.id === id))
            .filter(menu => menu !== undefined);
    } else {
        // Menu was removed - maintain order from favoriteMenuIds
        favoriteMenus.value = favoriteMenuIds.value
            .map(id => allMenus.value.find(menu => menu.id === id))
            .filter(menu => menu !== undefined);
    }
    
}, {
    immediate: true,
    deep: true
});

watch(isSortMode, (newVal) => {
    if (newVal) {
        nextTick(() => {
            initSortable();
        });
    } else {
        destroySortable();
    }
});

watch(() => favoriteMenus.value, () => {
    // Reinitialize sortable when menus change
    if (isSortMode.value && menuContainerRef.value) {
        nextTick(() => {
            destroySortable();
            initSortable();
        });
    }
}, { deep: true });

watch(() => props.floating, (newVal) => {
    if (newVal) {
        const savedPosition = loadPosition();
        if (savedPosition && savedPosition.x !== undefined && savedPosition.y !== undefined) {
            position.value = savedPosition;
        } else {
            position.value = calculateDefaultPosition();
        }
    } else {
        position.value = { x: 0, y: 0 };
    }
}, { immediate: true });

const updatePosition = (newPosition) => {
    position.value = newPosition;
    if (props.floating) {
        savePosition(newPosition);
    }
}

defineExpose({
    toggleMenu: (menuId) => {
        if (favoriteMenuIds.value.includes(menuId)) {
            favoriteMenuIds.value = favoriteMenuIds.value.filter(id => id !== menuId)
        } else {
            favoriteMenuIds.value.push(menuId)
        }
        // Maintain order from favoriteMenuIds array
        favoriteMenus.value = favoriteMenuIds.value
            .map(id => allMenus.value.find(menu => menu.id === id))
            .filter(menu => menu !== undefined);
        
        // Save the order to localStorage (order is preserved in the array)
        saveFavoriteMenuIds(favoriteMenuIds.value);
    },
    getFavoriteMenus: () => {
        return favoriteMenus.value;
    },
    getMenu: (menuId) => {
        return favoriteMenus.value.find(menu => menu.id === menuId);
    },
    getComponentRef: (menuId) => {
        return menuRefs.value[menuId];
    },
    setProps: (menuId, props) => {
        const menu = favoriteMenus.value.find(menu => menu.id === menuId);
        console.log('menu', menu);
        if (menu) {
            Object.entries(props).forEach(([key, value]) => {
                menu.props[key] = value;
            });
        }
    }
})
</script>

<style scoped>
.cursor-move {
    cursor: move;
}

.cursor-grabbing {
    cursor: grabbing;
}

.floating {
    @apply  z-[100] w-fit border-transparent rounded-md;
    padding: 0px;
    padding-left: 10px;
    padding-right: 10px;
    @apply bg-slate-700 dark:bg-gray-800;
}
</style>